import { useMemo } from "react";
import { Smartphone, Tablet, FileJson, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  type FormFactor,
  type HomeScreenLayout,
  type IconItem,
  type SmplifyExtension,
  type Target,
} from "./types";
import { buildConfig, getVariant, primaryFormFactor } from "./projection";

interface Props {
  ext: SmplifyExtension;
  /** Optional issue counts to surface readiness for each variant. */
  issuesByFf?: Partial<Record<FormFactor, { errors: number; warnings: number }>>;
}

interface VariantStat {
  ff: FormFactor;
  pages: number;
  dock: number;
  apps: number;
  webclips: number;
  folders: number;
  total: number;
}

function countItems(items: IconItem[] | undefined, acc: Omit<VariantStat, "ff" | "pages" | "dock">) {
  for (const it of items ?? []) {
    acc.total += 1;
    if (it.Type === "Application") acc.apps += 1;
    else if (it.Type === "WebClip") acc.webclips += 1;
    else if (it.Type === "Folder") {
      acc.folders += 1;
      // Recurse into folder pages so totals reflect every placed icon.
      for (const p of it.Pages ?? []) countItems(p, acc);
    }
  }
}

function statsFor(ff: FormFactor, layout: HomeScreenLayout): VariantStat {
  const base = { apps: 0, webclips: 0, folders: 0, total: 0 };
  for (const page of layout.Pages ?? []) countItems(page, base);
  countItems(layout.Dock, base);
  return {
    ff,
    pages: layout.Pages?.length ?? 0,
    dock: layout.Dock?.length ?? 0,
    ...base,
  };
}

export function ExportSummaryPanel({ ext, issuesByFf }: Props) {
  const target: Target = ext.target;
  const exportedFfs: FormFactor[] = useMemo(() => {
    if (target === "iphone") return ["iphone"];
    if (target === "ipad") return ["ipad"];
    return ["iphone", "ipad"];
  }, [target]);

  const primaryFf = primaryFormFactor(target);
  const builtConfig = useMemo(() => buildConfig(ext), [ext]);
  const payloadKey = "com.apple.homescreenlayout";
  const sizeBytes = useMemo(
    () => new Blob([JSON.stringify(builtConfig)]).size,
    [builtConfig]
  );

  return (
    <section
      aria-label="Export summary"
      className="rounded-lg border bg-card p-4 space-y-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <FileJson className="h-4 w-4 text-muted-foreground" />
            Export preview
          </h4>
          <p className="text-xs text-muted-foreground">
            What will be sent for{" "}
            <code className="font-mono text-[11px] px-1 py-0.5 rounded bg-muted">
              {payloadKey}
            </code>{" "}
            when you deploy.
          </p>
        </div>
        <Badge variant="outline" className="shrink-0 text-[11px]">
          {exportedFfs.length} variant{exportedFfs.length === 1 ? "" : "s"}
        </Badge>
      </header>

      <Separator />

      <ul className="space-y-3">
        {exportedFfs.map((ff) => {
          const layout = getVariant(ext, ff);
          const s = statsFor(ff, layout);
          const issues = issuesByFf?.[ff];
          const Icon = ff === "iphone" ? Smartphone : Tablet;
          const isPrimary = ff === primaryFf;
          const hasErrors = (issues?.errors ?? 0) > 0;
          const hasWarnings = (issues?.warnings ?? 0) > 0;
          return (
            <li key={ff} className="rounded-md border bg-muted/20 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {ff === "iphone" ? "iPhone" : "iPad"}
                  {isPrimary && (
                    <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                      Primary payload
                    </Badge>
                  )}
                  {!isPrimary && target === "both" && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      Sibling policy
                    </Badge>
                  )}
                </div>
                {hasErrors ? (
                  <span className="flex items-center gap-1 text-xs text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {issues!.errors} error{issues!.errors === 1 ? "" : "s"}
                  </span>
                ) : hasWarnings ? (
                  <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {issues!.warnings} warning{issues!.warnings === 1 ? "" : "s"}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-500">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Ready
                  </span>
                )}
              </div>

              <dl className="grid grid-cols-3 gap-2 text-xs">
                <div className="space-y-0.5">
                  <dt className="text-muted-foreground">Pages</dt>
                  <dd className="font-medium tabular-nums">{s.pages}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-muted-foreground">Dock</dt>
                  <dd className="font-medium tabular-nums">
                    {s.dock} / {ff === "iphone" ? 4 : 6}
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-muted-foreground">Total icons</dt>
                  <dd className="font-medium tabular-nums">{s.total}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-muted-foreground">Apps</dt>
                  <dd className="font-medium tabular-nums">{s.apps}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-muted-foreground">Web clips</dt>
                  <dd className="font-medium tabular-nums">{s.webclips}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-muted-foreground">Folders</dt>
                  <dd className="font-medium tabular-nums">{s.folders}</dd>
                </div>
              </dl>
            </li>
          );
        })}
      </ul>

      <Separator />

      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          Payload key:{" "}
          <code className="font-mono">{payloadKey}</code>
        </span>
        <span className="tabular-nums">{(sizeBytes / 1024).toFixed(1)} KB</span>
      </div>
    </section>
  );
}
